import React, { useEffect, useState } from 'react';
import ledge from 'assets/images/ledge.png';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import WithQuery from 'components/layout/WithQuery';
import fetchAsText from 'util/fetch-as-text-util';
import BookCover from './BookCover';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { withAsyncErrorHandler } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/error-util';
import { BookType } from '@monorepo/shared/src/graph/schema-generated-types-student';
import { BOOK_COVER_HEIGHT, BookCoverProps, BOOK_COVER_WIDTH, SHELF_GAP } from 'types';
import { formatCovers, getCover } from '@monorepo/shared/src/util/book-util';
import useAudioInstructions from 'state/hooks/use-audio-instructions';
import useBookAccess from 'state/hooks/use-book-access';

type Props = {
  bookType: BookType;
};

const BookShelf: React.FC<Props> = ({ bookType }) => {
  const classes = styles();
  const [{ books, instructions, loading: loadingLetterBooks, error }] = usePageStore(bookType);
  const [loadingBookCovers, setLoadingBookCovers] = useState(true);
  const [bookCovers, setBookCovers] = useState<BookCoverProps[]>([]);

  const {
    accessBookAfterAssignment,
    accessibleBooks,
    loading: bookAccessLoading,
  } = useBookAccess();

  const fetchBookCoversHtml = async (books) => {
    const bookCoversHtmlText = await withAsyncErrorHandler(() =>
      Promise.allSettled(books.map((book) => fetchAsText(getCover(book).src)))
    );

    setBookCovers(formatCovers(bookCoversHtmlText, books));
    setLoadingBookCovers(false);
  };

  useEffect(() => {
    if (!loadingLetterBooks && books.length) {
      fetchBookCoversHtml(books);
    }
  }, [loadingLetterBooks, books]);

  useAudioInstructions(
    instructions,
    !loadingLetterBooks && !loadingBookCovers && !error && bookCovers.length
  );

  return (
    <WithQuery
      loading={loadingLetterBooks || loadingBookCovers || bookAccessLoading}
      error={error || (!loadingBookCovers && !bookCovers.length)}
    >
      <Box className={classes.grid}>
        {bookCovers.map((cover) => (
          <BookCover
            key={cover.id}
            disabled={accessBookAfterAssignment && !accessibleBooks.includes(cover.id)}
            {...cover}
          />
        ))}
      </Box>
    </WithQuery>
  );
};

const styles = makeStyles((theme) => ({
  grid: {
    width: '100%',
    backgroundImage: `url(${ledge})`,
    backgroundRepeat: 'repeat-y',
    backgroundSize: `100% ${theme.typography.pxToRem(BOOK_COVER_HEIGHT + SHELF_GAP)}`,
    display: 'grid',
    justifyContent: 'space-evenly',
    gridTemplateColumns: `repeat(auto-fill, ${theme.typography.pxToRem(BOOK_COVER_WIDTH)})`,
    gridTemplateRows: theme.typography.pxToRem(BOOK_COVER_HEIGHT),
    gridGap: `${theme.typography.pxToRem(SHELF_GAP)} ${theme.typography.pxToRem(18)}`,
    paddingBottom: theme.typography.pxToRem(SHELF_GAP),
  },
}));

export default BookShelf;
